import { MutationFunctionOptions, useMutation } from "@apollo/client";
import { Box, Flex, SimpleGrid, Text } from "@govlaunch/web";
import React, { useCallback, useContext, useState } from "react";
import { FormattedMessage } from "react-intl";
import { AuthContext, IAuthContext } from "~/components/auth/Auth";
import SignUpPageSeo from "~/components/seo/SignUpPageSeo";
import SignInMutation from "~/lib/mutations/SignInMutation";
import { ISignInMutation, ISignInMutationVariables } from "~/lib/mutations/__generated__/SignInMutation.generated";
import SelfieQuery from "~/lib/queries/SelfieQuery";
import { useIntlLocale } from "~/lib/withIntl";
import globalOnSignIn from "~/src/components/auth/globalOnSignIn";
import OAuthFacebook from "~/src/components/auth/OAuthFacebook";
import OAuthLinkedIn from "~/src/components/auth/OAuthLinkedIn";
import OAuthTwitter from "~/src/components/auth/OAuthTwitter";
import SignInBlocked from "~/src/components/auth/SignInBlocked";
import SignInSlogan from "~/src/components/auth/SignInSlogan";
import SignInSwitchLink from "~/src/components/auth/SignInSwitchLink";

interface ISignInPageDesktopProps {
  signingIn?: boolean;
  inviteToken?: string | null;
}

export default function SignInPageDesktop({ signingIn = true, inviteToken }: ISignInPageDesktopProps) {
  const [isBlocked, setIsBlocked] = useState(false);
  const { login } = useContext<IAuthContext>(AuthContext);
  const { locale, locales, changeLocale } = useIntlLocale();

  const [signIn] = useMutation<ISignInMutation, ISignInMutationVariables>(SignInMutation, {
    onCompleted: ({ signIn: data }) => {
      if (data && data.tokens && data.tokens.token) {
        login(data.tokens.token);

        if (
          data.user &&
          data.user.language &&
          locale !== data.user.language &&
          locales.map(({ id }) => id).includes(data.user.language)
        ) {
          changeLocale(data.user.language);
        }
      }
    },
    refetchQueries: [
      "Homepage",
      "SearchProjects",
      "ProjectsByGovernment",
      "ProjectsByIds",
      "ProjectBySlug",
      "StoryBySlug",
    ],
    update: (proxy, { data }) => {
      if (data && data.signIn) {
        proxy.writeQuery({
          query: SelfieQuery,
          variables: {
            token: inviteToken || null,
          },
          data: {
            __typename: "Query",
            selfie: {
              ...data.signIn.user,
            },
          },
        });
      }
    },
  });

  const createOnSignInHandler = useCallback((provider: "linkedin" | "twitter" | "facebook") => {
    return async (options?: MutationFunctionOptions<ISignInMutation, ISignInMutationVariables> | undefined) => {
      const signInResult = await signIn(options);
      if (signInResult.data?.signIn) {
        globalOnSignIn(provider, signInResult.data?.signIn);
      }
      return signInResult;
    };
  }, []);

  return (
    <>
      <SignUpPageSeo signingIn={signingIn} />
      <SimpleGrid columns={[2, 2, 2, 2]} width="100%" gridTemplateColumns="50% 50%">
        <Box
          display="flex"
          backgroundImage="url('/static/signin/signin-town-hall.png')"
          width="100%"
          height="90vh"
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          overflow="hidden"
        >
          <SignInSlogan ml="50px" mb="66px" signingIn={signingIn} />
        </Box>

        <Flex mt="15%" justifyContent="center" px="16px" mb="20px">
          <Flex direction="column" alignItems="flex-start">
            <Text color="#2F80ED" fontSize="lg" fontWeight="semibold" lineHeight="24px" textTransform="uppercase">
              {signingIn ? (
                <FormattedMessage defaultMessage="Welcome Back!" id="N5WoR+" />
              ) : (
                <FormattedMessage defaultMessage="JOIN FOR FREE" id="kf9pAr" />
              )}
            </Text>
            <Text fontSize="4xl" fontWeight="semibold" lineHeight="42px" as="h1">
              {signingIn ? (
                <FormattedMessage defaultMessage="Log in to Govlaunch" id="iqah/X" />
              ) : (
                <FormattedMessage defaultMessage="Sign up for Govlaunch" id="rT5edK" />
              )}
            </Text>

            <SignInSwitchLink signIn={signingIn} mt="24px" />

            {isBlocked ? (
              <SignInBlocked />
            ) : (
              <Flex direction="column" mt="30px" width="100%">
                <OAuthLinkedIn
                  signIn={createOnSignInHandler("linkedin")}
                  inviteToken={inviteToken}
                  setBlocked={setIsBlocked}
                />
                {false && (
                  <OAuthTwitter
                    signIn={createOnSignInHandler("twitter")}
                    inviteToken={inviteToken}
                    setBlocked={setIsBlocked}
                    mt="24px"
                  />
                )}
                {false && (
                  <OAuthFacebook
                    signIn={createOnSignInHandler("facebook")}
                    inviteToken={inviteToken}
                    setBlocked={setIsBlocked}
                    mt="24px"
                  />
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </SimpleGrid>
    </>
  );
}
